import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, apiGet } from "../api";
import { toast } from "sonner";
import { Upload, Loader2, RefreshCw, Filter, Download, Plus, Trash2, Link2 } from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";
import { formatCurrency as fmt } from "../lib/format";
import MonthPicker from "../components/MonthPicker";

// Resolution reason labels (the three fixed categories).
const REASON_LABELS = { uom: "Unit of Measure", fx: "Exchange rate", obsolete: "Obsolete" };

const InternalPriceComparison = () => {
  usePageTitle("Internal Price Comparison");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("comparison");
  const [supplier, setSupplier] = useState("all");
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [resFilter, setResFilter] = useState("open"); // open | resolved | all

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMonth, setUploadMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  const loadSnapshots = useCallback(async () => {
    const d = await apiGet("/internal-prices/snapshots").catch(() => null);
    setSnapshots(Array.isArray(d?.snapshots) ? d.snapshots : []);
  }, []);

  const loadComparison = useCallback(async (sup) => {
    setLoading(true);
    setError(false);
    try {
      const q = sup && sup !== "all" ? `?supplier=${encodeURIComponent(sup)}` : "";
      const d = await apiGet(`/internal-prices/comparison${q}`, { timeout: 90000 });
      const items = Array.isArray(d?.items) ? d.items : [];
      setRows(items);
      setTotal(d?.total ?? items.length);
      setSnapshot(d?.reference_snapshot ?? null);
      // Build supplier dropdown from an unfiltered load only
      if (!sup || sup === "all") {
        setSupplierOptions([...new Set(items.map((r) => r.supplier).filter(Boolean))].sort());
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComparison("all");
    loadSnapshots();
  }, [loadComparison, loadSnapshots]);

  const onSupplierChange = (val) => {
    setSupplier(val);
    loadComparison(val);
  };

  // Always exports the FULL comparison across all suppliers, regardless of the
  // on-screen filter (per spec: "everything, unfiltered").
  const exportData = async (fmt = "csv") => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`${API}/internal-prices/export?fmt=${fmt}`, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `internal_price_comparison_${new Date().toISOString().slice(0, 10)}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Comparison exported");
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error("Choose a KF / Minster CSV first");
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(uploadMonth)) {
      toast.error("Month must be YYYY-MM");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const res = await fetch(`${API}/internal-prices/upload?snapshot=${encodeURIComponent(uploadMonth)}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const j = await res.json();
      toast.success(`Loaded ${uploadMonth}: ${j.kraken_field_rows} KF, ${j.wms_rows} WMS`);
      setUploadFile(null);
      await loadSnapshots();
      await loadComparison(supplier);
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const maxSpread = useMemo(() => (rows.length ? rows[0].spread : 0), [rows]);

  // Resolution filter: Open = unresolved OR re-surfaced (spread moved); Resolved
  // = dismissed and still valid; All = everything.
  const displayedRows = useMemo(() => {
    if (resFilter === "all") return rows;
    if (resFilter === "resolved") return rows.filter((r) => r.resolution && r.resolution_status === "resolved");
    return rows.filter((r) => !r.resolution || r.resolution_status === "resurfaced");
  }, [rows, resFilter]);

  const openCount = useMemo(() => rows.filter((r) => !r.resolution || r.resolution_status === "resurfaced").length, [rows]);
  const resolvedCount = rows.length - openCount;

  // Patch a single row in local state so a resolve/re-open updates instantly
  // WITHOUT rebuilding the whole comparison (which is expensive).
  const patchRow = (r, patch) =>
    setRows((prev) => prev.map((x) => (x.supplier === r.supplier && x.sku === r.sku ? { ...x, ...patch } : x)));

  const resolveRow = async (r, reason) => {
    patchRow(r, { resolution: reason, resolution_status: "resolved" }); // optimistic
    try {
      await axios.post(`${API}/internal-prices/resolutions`, { supplier: r.supplier, sku: r.sku, reason, spread: r.spread });
      toast.success(`Marked as ${REASON_LABELS[reason]}`);
    } catch {
      patchRow(r, { resolution: r.resolution ?? null, resolution_status: r.resolution_status ?? null }); // revert
      toast.error("Could not save resolution");
    }
  };
  const reopenRow = async (r) => {
    const prevRes = r.resolution, prevStatus = r.resolution_status;
    patchRow(r, { resolution: null, resolution_status: null }); // optimistic
    try {
      await axios.delete(`${API}/internal-prices/resolutions`, { params: { supplier: r.supplier, sku: r.sku } });
      toast.success("Re-opened");
    } catch {
      patchRow(r, { resolution: prevRes, resolution_status: prevStatus }); // revert
      toast.error("Could not re-open");
    }
  };

  return (
    <div data-testid="internal-price-page" className="page-container" style={{ padding: "1rem", maxWidth: 1400, minWidth: 0, margin: "0 auto" }}>
      <div style={{ marginBottom: "1rem" }}>
        <h1 className="page-title">Internal Price Comparison</h1>
        <p className="page-subtitle">
          The same product's price across the three systems that hold pricing data: price file, WMS (Minster) and Kraken Field.
          These should all match, so any spread is a data discrepancy to investigate, not a cheaper source. Sorted by biggest disagreement.
          Shows only products priced in the price file and both WMS and Kraken Field. Standalone view — does not affect dashboards or reports.
        </p>
      </div>

      {/* Monthly reference-price upload */}
      <div className="card" style={{ marginBottom: "1.5rem" }} data-testid="internal-upload-card">
        <h2 style={{ fontSize: "1.05rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Upload size={18} /> Monthly WMS / KF upload
        </h2>
        <p style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "0.9rem" }}>
          Upload the combined KF + Minster report each month. Each upload is kept as a dated snapshot; the comparison uses the latest.
          {snapshot ? ` Currently comparing against ${snapshot}.` : " No reference snapshot loaded yet."}
        </p>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <MonthPicker value={uploadMonth} onChange={setUploadMonth} testId="internal-upload-month" />
          <input type="file" accept=".csv" onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            data-testid="internal-upload-file" style={{ fontSize: "0.85rem", color: "var(--octopus-voltage)" }} />
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading} data-testid="internal-upload-btn">
            {uploading ? <Loader2 size={15} style={{ animation: "spin 0.8s linear infinite", marginRight: "0.4rem" }} /> : <Upload size={15} style={{ marginRight: "0.4rem" }} />}
            Upload
          </button>
        </div>
        {snapshots.length > 0 && (
          <div style={{ marginTop: "0.9rem", fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>
            Snapshots: {snapshots.map((s) => `${s.snapshot} (${s.rows})`).join("  ·  ")}
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.25rem" }}>
        <button className={tab === "comparison" ? "btn btn-primary" : "btn btn-secondary"} onClick={() => setTab("comparison")} data-testid="internal-tab-comparison">Comparison</button>
        <button className={tab === "mappings" ? "btn btn-primary" : "btn btn-secondary"} onClick={() => setTab("mappings")} data-testid="internal-tab-mappings">
          <Link2 size={14} style={{ marginRight: "0.35rem" }} /> Manual Mappings
        </button>
      </div>

      {tab === "mappings" && <MappingsPanel />}

      {tab === "comparison" && (<>
      {/* Filter + summary */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
          <Filter size={16} style={{ color: "var(--octopus-voltage)" }} />
          <select className="form-input" value={supplier} onChange={(e) => onSupplierChange(e.target.value)}
            style={{ maxWidth: 240 }} data-testid="internal-supplier-filter">
            <option value="all">All suppliers</option>
            {supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={() => loadComparison(supplier)} data-testid="internal-refresh">
            <RefreshCw size={14} style={{ marginRight: "0.35rem" }} /> Refresh
          </button>
          <button className="btn btn-secondary" onClick={() => exportData("csv")} disabled={exporting} data-testid="internal-export">
            {exporting
              ? <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite", marginRight: "0.35rem" }} />
              : <Download size={14} style={{ marginRight: "0.35rem" }} />}
            Export CSV
          </button>
          <button className="btn btn-secondary" onClick={() => exportData("xlsx")} disabled={exporting} data-testid="internal-export-xlsx">
            <Download size={14} style={{ marginRight: "0.35rem" }} /> Export Excel
          </button>
        </div>
        <div style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>
          {total.toLocaleString()} products where systems disagree · largest spread {fmt(maxSpread)}
        </div>
      </div>

      {/* Resolution filter — Open / Resolved / All */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
        {[["open", `Open (${openCount})`], ["resolved", `Resolved (${resolvedCount})`], ["all", `All (${rows.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setResFilter(k)}
            className={resFilter === k ? "btn btn-primary" : "btn btn-secondary"}
            style={{ fontSize: "0.8rem", padding: "0.4rem 0.9rem" }} data-testid={`internal-resfilter-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="card" style={{ padding: "2rem", textAlign: "center", color: "var(--octopus-pink)" }}>
          Failed to load comparison. <button className="btn btn-secondary" style={{ marginLeft: "0.5rem" }} onClick={() => loadComparison(supplier)}>Retry</button>
        </div>
      ) : loading ? (
        <div className="card" style={{ padding: "3rem", textAlign: "center", color: "var(--octopus-voltage)" }}>
          <Loader2 size={28} style={{ animation: "spin 0.8s linear infinite" }} />
          <p style={{ marginTop: "0.75rem", fontSize: "0.85rem" }}>Building comparison…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ padding: "3rem", textAlign: "center", color: "var(--octopus-voltage)", opacity: 0.6 }}>
          No comparable products. Upload a WMS / KF report above to populate references.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="data-table" data-testid="internal-comparison-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>SKU</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Price File</th>
                  <th style={{ textAlign: "right" }}>WMS</th>
                  <th style={{ textAlign: "right" }}>KF</th>
                  <th style={{ textAlign: "right" }}>Spread</th>
                  <th style={{ textAlign: "right" }}>Status / Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: "2rem", color: "var(--octopus-voltage)", opacity: 0.6 }}>
                    {resFilter === "resolved" ? "No resolved items yet." : "Nothing in this view."}
                  </td></tr>
                )}
                {displayedRows.map((r, i) => (
                  <tr key={`${r.supplier}-${r.sku}-${i}`} data-testid={`internal-row-${i}`}>
                    <td style={{ fontSize: "0.8rem", color: "var(--octopus-voltage)" }}>{r.supplier}</td>
                    <td style={{ fontWeight: 600, color: "var(--octopus-ice)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{r.sku || "—"}</td>
                    <td style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.8, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.description}</td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "var(--octopus-ice)", fontSize: "0.8rem" }}>{fmt(r.price_file_price)}</td>
                    <td style={{ textAlign: "right", fontSize: "0.8rem", color: "var(--octopus-voltage)" }}>{fmt(r.wms_price)}</td>
                    <td style={{ textAlign: "right", fontSize: "0.8rem", color: "var(--octopus-voltage)" }}>{fmt(r.kf_price)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: "0.82rem", color: "#F050F8" }} title={`${r.prices_present} of 3 systems have a price`}>
                      {fmt(r.spread)}
                    </td>
                    <td style={{ textAlign: "right", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                      {r.resolution_status === "resolved" ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                          <span className="badge badge-success" style={{ fontSize: "0.68rem" }}>{REASON_LABELS[r.resolution] || r.resolution}</span>
                          <button onClick={() => reopenRow(r)} data-testid={`internal-reopen-${i}`}
                            style={{ background: "none", border: "none", color: "var(--octopus-voltage)", cursor: "pointer", fontSize: "0.72rem", textDecoration: "underline" }}>Re-open</button>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                          {r.resolution_status === "resurfaced" && (
                            <span title={`Was ${REASON_LABELS[r.resolution] || r.resolution} — spread has since changed`} style={{ color: "#FF9F0A" }}>⚠</span>
                          )}
                          <select
                            value=""
                            onChange={(e) => e.target.value && resolveRow(r, e.target.value)}
                            data-testid={`internal-resolve-${i}`}
                            style={{ background: "#17102b", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, color: "var(--octopus-ice)", fontSize: "0.72rem", padding: "0.25rem 0.4rem", cursor: "pointer" }}
                          >
                            <option value="" style={{ background: "#17102b", color: "#9d8fc0" }}>{r.resolution_status === "resurfaced" ? "Re-resolve…" : "Resolve…"}</option>
                            <option value="uom" style={{ background: "#17102b", color: "#f5f2fb" }}>Unit of Measure</option>
                            <option value="fx" style={{ background: "#17102b", color: "#f5f2fb" }}>Exchange rate</option>
                            <option value="obsolete" style={{ background: "#17102b", color: "#f5f2fb" }}>Obsolete</option>
                          </select>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
};

function MappingsPanel() {
  const [mappings, setMappings] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const blank = { canonical: "", wms_code: "", kf_code: "", invoice_sku: "", price_file_sku: "", note: "" };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        apiGet("/internal-prices/mappings"),
        apiGet("/internal-prices/unmatched", { timeout: 120000 }),
      ]);
      setMappings(m.mappings || []);
      setUnmatched(u.unmatched || []);
    } catch { toast.error("Failed to load mappings"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    if (!payload.canonical?.trim()) { toast.error("Canonical part number is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/internal-prices/mappings`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      toast.success("Mapping saved");
      setForm(blank);
      await load();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      const res = await fetch(`${API}/internal-prices/mappings/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  // Prefill the form from an unmatched code (+ optional candidate full code) so
  // the user reviews, then Saves.
  const prefill = (u, candidate) => {
    setForm({
      canonical: candidate || u.code,
      wms_code: u.source === "wms" ? u.code : "",
      kf_code: u.source === "kraken_field" ? u.code : "",
      invoice_sku: "",
      price_file_sku: candidate || "",
      note: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const F = (k, ph) => (
    <input className="form-input" placeholder={ph} value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      style={{ fontSize: "0.82rem", minWidth: 120 }} data-testid={`map-${k}`} />
  );

  return (
    <div>
      {/* Add / edit form */}
      <div className="card" style={{ marginBottom: "1.25rem" }}>
        <h2 style={{ fontSize: "1.05rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Plus size={18} /> Add a mapping
        </h2>
        <p style={{ fontSize: "0.82rem", color: "var(--octopus-voltage)", opacity: 0.75, marginBottom: "0.9rem" }}>
          Tie each system's code to one canonical part number. Fill the canonical (the true/full code) plus whichever system variants differ — e.g. the shortened Minster (WMS) code. Blank fields are ignored.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {F("canonical", "Canonical part no. *")}
          {F("wms_code", "Minster / WMS code")}
          {F("kf_code", "Kraken Field code")}
          {F("invoice_sku", "Invoice SKU")}
          {F("price_file_sku", "Price file SKU")}
          {F("note", "Note (optional)")}
          <button className="btn btn-primary" onClick={() => save(form)} disabled={saving} data-testid="map-save">
            {saving ? <Loader2 size={15} style={{ animation: "spin 0.8s linear infinite", marginRight: "0.4rem" }} /> : <Plus size={15} style={{ marginRight: "0.4rem" }} />}
            Save mapping
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: "3rem", textAlign: "center", color: "var(--octopus-voltage)" }}>
          <Loader2 size={28} style={{ animation: "spin 0.8s linear infinite" }} />
          <p style={{ marginTop: "0.75rem", fontSize: "0.85rem" }}>Loading mappings…</p>
        </div>
      ) : (<>
        {/* Existing mappings */}
        <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
          <div style={{ padding: "0.9rem 1rem", fontSize: "0.9rem", fontWeight: 600, color: "var(--octopus-ice)" }}>
            {mappings.length} mapping{mappings.length !== 1 ? "s" : ""}
          </div>
          {mappings.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead><tr>
                  <th>Canonical</th><th>Minster/WMS</th><th>Kraken Field</th><th>Invoice SKU</th><th>Price file SKU</th><th>Note</th><th></th>
                </tr></thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.id} data-testid={`map-row-${m.id}`}>
                      <td style={{ fontWeight: 600, color: "var(--octopus-ice)", fontSize: "0.8rem" }}>{m.canonical}</td>
                      <td style={{ fontSize: "0.8rem" }}>{m.wms_code || "—"}</td>
                      <td style={{ fontSize: "0.8rem" }}>{m.kf_code || "—"}</td>
                      <td style={{ fontSize: "0.8rem" }}>{m.invoice_sku || "—"}</td>
                      <td style={{ fontSize: "0.8rem" }}>{m.price_file_sku || "—"}</td>
                      <td style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.8 }}>{m.note || ""}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn btn-secondary" onClick={() => remove(m.id)} data-testid={`map-del-${m.id}`} style={{ padding: "0.25rem 0.5rem" }}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Unmatched reference codes */}
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "0.9rem 1rem" }}>
            <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--octopus-ice)" }}>
              {unmatched.length} unmatched WMS / KF code{unmatched.length !== 1 ? "s" : ""}
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>
              Reference codes that don't join to any price file — likely Minster truncations. Click a suggested full code to prefill a mapping.
            </div>
          </div>
          {unmatched.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead><tr><th>Supplier</th><th>Code</th><th>Source</th><th style={{ textAlign: "right" }}>Price</th><th>Suggested full codes</th></tr></thead>
                <tbody>
                  {unmatched.map((u, i) => (
                    <tr key={`${u.supplier}-${u.code}-${u.source}-${i}`} data-testid={`unmatched-${i}`}>
                      <td style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)" }}>{u.supplier || "—"}</td>
                      <td style={{ fontWeight: 600, color: "var(--octopus-ice)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{u.code}</td>
                      <td style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)" }}>{u.source === "wms" ? "Minster/WMS" : "Kraken Field"}</td>
                      <td style={{ textAlign: "right", fontSize: "0.8rem" }}>{u.unit_price != null ? `£${Number(u.unit_price).toFixed(2)}` : "—"}</td>
                      <td>
                        <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                          {(u.candidates || []).length === 0
                            ? <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }} onClick={() => prefill(u, "")}>Map manually</button>
                            : u.candidates.map((c) => (
                              <button key={c} className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }} onClick={() => prefill(u, c)} title="Prefill a mapping with this full code">
                                {c}
                              </button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>)}
    </div>
  );
}

export default InternalPriceComparison;
